import React, { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { Close, Save } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress
} from '@mui/material';
import { Box, Button, Container, Grid, Typography } from '@material-ui/core';

import { getEmaLabelSeparation, uploadEmaLabelSeparation } from '../../api/ema';
import Store from '../../store';
import Actions from '../../store/actions';
import AuthContext from '../../store/Auth/AuthProvider';
import HttpStatus from '../../types/enum/http_status';
import type {
  EmaLabelSeparationAuthContext,
  EmaLabelSeparationDocument,
  EmaLabelSeparationLoadingProps,
  EmaLabelSeparationLocationState,
  EmaLabelSeparationRouteParams,
  EmaLabelSeparationStoreContext
} from './EMALabelSeparation.types';
import Styles from './EMALabelSeparation.styles';
import { getEmaLabelSeparationError, normalizeEmaLabelSections } from './EMALabelSeparation.utils';

export const Loading = memo(({ dataLoading, dataSubmitting }: EmaLabelSeparationLoadingProps) => {
  const classes = Styles();

  return (
    <Box className={classes.loadingRoot}>
      <LinearProgress className={classes.loadingBar} color='secondary' />
      <Typography className={classes.notAvailable}>
        {dataLoading ? 'Loading data ....' : 'Submitting changes ....'}
      </Typography>
      {dataSubmitting && (
        <Typography className={classes.notAvailable}>
          (this might take a couple of minutes to complete)
        </Typography>
      )}
    </Box>
  );
});

const EMACurationPageSeparation = () => {
  const { curationId } = useParams<EmaLabelSeparationRouteParams>();
  const history = useHistory();
  const classes = Styles();
  const { dispatch: globalDispatch } = useContext(
    Store
  ) as unknown as EmaLabelSeparationStoreContext;
  const { currentUser } = useContext(AuthContext) as unknown as EmaLabelSeparationAuthContext;
  const [isSubmitting, setSubmitting] = useState(false);
  const [openSubmitConfirmation, setOpenSubmitConfirmation] = useState(false);
  const [documentData, setDocumentData] = useState<EmaLabelSeparationDocument | null>(null);
  const loading = documentData === null;
  const mountedRef = useRef(true);
  const requestVersionRef = useRef(0);
  const popupTimerRef = useRef<number | null>(null);

  const showAlert = useCallback(
    (message: string) => {
      globalDispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message }
      });
    },
    [globalDispatch]
  );

  const clearPopupTimer = useCallback(() => {
    if (popupTimerRef.current !== null) {
      window.clearInterval(popupTimerRef.current);
      popupTimerRef.current = null;
    }
  }, []);

  const handleOpenPopup = useCallback(
    (url: string) => {
      clearPopupTimer();
      const options =
        'height=1000,width=1000,left=100,top=100,resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,directories=no,status=yes';
      const popup = window.open(url, '_blank', options);

      if (!popup) {
        showAlert('The OneDrive popup was blocked. Allow popups, then use Open file again.');
        return;
      }

      popupTimerRef.current = window.setInterval(() => {
        if (popup.closed) {
          clearPopupTimer();
          if (mountedRef.current) {
            setOpenSubmitConfirmation(true);
          }
        }
      }, 1000);
    },
    [clearPopupTimer, showAlert]
  );

  useEffect(() => {
    mountedRef.current = true;
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;

    const loadDocument = async () => {
      if (!curationId) {
        showAlert('A curation ID is required for label separation.');
        history.replace('/ema/curations');
        return;
      }

      const response = await getEmaLabelSeparation(curationId);
      const responseBody = response?.data?.body;

      if (
        response?.status !== HttpStatus.OK ||
        !responseBody ||
        typeof responseBody === 'string' ||
        !responseBody.onedrive_url
      ) {
        throw new Error(
          getEmaLabelSeparationError(
            responseBody,
            response?.data?.message || 'Unable to load the label separation document.'
          )
        );
      }

      if (!mountedRef.current || requestVersion !== requestVersionRef.current) {
        return;
      }

      const nextDocument: EmaLabelSeparationDocument = {
        ...responseBody,
        labels: normalizeEmaLabelSections(responseBody.labels)
      };
      setDocumentData(nextDocument);
      handleOpenPopup(nextDocument.onedrive_url);
    };

    loadDocument().catch(error => {
      if (mountedRef.current && requestVersion === requestVersionRef.current) {
        showAlert(
          error instanceof Error ? error.message : 'Unable to load the label separation document.'
        );
        history.replace('/ema/curations');
      }
    });

    return () => {
      mountedRef.current = false;
      requestVersionRef.current += 1;
      clearPopupTimer();
    };
  }, [clearPopupTimer, curationId, handleOpenPopup, history, showAlert]);

  const handleSubmitChanges = useCallback(async () => {
    if (!documentData || isSubmitting) {
      return;
    }
    if (!currentUser?.email || !currentUser?.['custom:user']) {
      showAlert('Curator name and email are required to upload label separation.');
      return;
    }

    clearPopupTimer();
    setOpenSubmitConfirmation(false);
    setSubmitting(true);

    try {
      const response = await uploadEmaLabelSeparation(curationId, {
        curator_email: currentUser.email,
        curator_name: currentUser['custom:user']
      });
      const responseBody = response?.data?.body;

      if (response?.status !== HttpStatus.OK || !responseBody || typeof responseBody === 'string') {
        if (mountedRef.current) {
          showAlert(
            getEmaLabelSeparationError(
              responseBody,
              response?.data?.message || 'Unable to upload the edited label separation document.'
            )
          );
          setSubmitting(false);
        }
        return;
      }

      const labelSeparation: EmaLabelSeparationDocument = {
        ...documentData,
        product_number: responseBody.product_number || documentData.product_number,
        s3_url: responseBody.s3_url || documentData.s3_url
      };
      const locationState: EmaLabelSeparationLocationState = {
        curationId,
        labelSeparation
      };

      history.push(
        `/ema/curations/${encodeURIComponent(curationId)}/label-separation/labels`,
        locationState
      );
    } catch {
      if (mountedRef.current) {
        showAlert('Unable to upload the edited label separation document.');
        setSubmitting(false);
      }
    }
  }, [clearPopupTimer, curationId, currentUser, documentData, history, isSubmitting, showAlert]);

  const reopenDocument = useCallback(() => {
    if (!documentData) {
      return;
    }
    setOpenSubmitConfirmation(false);
    handleOpenPopup(documentData.onedrive_url);
  }, [documentData, handleOpenPopup]);

  return (
    <Box className={classes.root}>
      <Container maxWidth='lg' className={classes.container}>
        <Box className={classes.toolbar}>
          <Button
            className={classes.button}
            disabled={loading || isSubmitting}
            onClick={() => history.push('/ema/curations')}
            variant='contained'>
            Go back
          </Button>
        </Box>
        <Grid container spacing={2}>
          {loading || isSubmitting ? (
            <Loading dataLoading={loading} dataSubmitting={isSubmitting} />
          ) : (
            <>
              <Grid item xs={12} lg={6}>
                {documentData?.onedrive_url && (
                  <Box className={`${classes.pdfContainer} ${classes.editablePanel}`}>
                    <Typography variant='h5' component='div' className={classes.notAvailable}>
                      Editable file is open in a new popup window
                    </Typography>
                    <Typography variant='body1' component='div' className={classes.note}>
                      <b>Note:</b>
                      <ul>
                        <li>You can press CTRL + ENTER to add page breaks.</li>
                        <li>
                          After you close the document window, confirm that your changes are ready
                          to upload.
                        </li>
                      </ul>
                    </Typography>
                    <Button className={classes.button} onClick={reopenDocument} variant='contained'>
                      Open file again
                    </Button>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} lg={6}>
                {documentData?.s3_url ? (
                  <Box display='flex' className={classes.pdfContainer}>
                    <iframe
                      width='100%'
                      height='100%'
                      title='EMA label separation PDF'
                      src={documentData.s3_url}
                      className={classes.pdfEma}
                    />
                  </Box>
                ) : (
                  <Box
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    className={classes.pdfContainer}>
                    <Typography className={classes.notAvailable}>No PDF is available</Typography>
                  </Box>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Container>

      <Dialog
        open={openSubmitConfirmation}
        aria-labelledby='ema-label-upload-dialog-title'
        aria-describedby='ema-label-upload-dialog-description'>
        <DialogTitle id='ema-label-upload-dialog-title'>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <div>Confirm submission</div>
            <IconButton aria-label='close' onClick={() => setOpenSubmitConfirmation(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='ema-label-upload-dialog-description'>
            Are you sure you have edited the document accurately?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            className={classes.button}
            disabled={isSubmitting}
            onClick={reopenDocument}>
            Open file again
          </Button>
          <Button
            startIcon={<Save />}
            variant='contained'
            onClick={handleSubmitChanges}
            disabled={isSubmitting}
            color='secondary'
            className={classes.button}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default memo(EMACurationPageSeparation);
